// --- React
import { useCallback, useMemo, useState } from 'react';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
// --- Hooks
import { useUsersData } from '../../../hooks/useUsersData';

/**
 * Custom hook for user grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the users grid
 * - Fetches sorted user data from the API via useUsersData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @returns {object} { setSort, columns }
 */
export function useUserGridUtils({ columnHandlers = [] } = {}) {
    const { fetchUsers, updateUsers, userRange } = useUsersData();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update users
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            const data = await fetchUsers(userRange, null, customSort);

            if (data) {
                updateUsers({
                    users: data?.content,
                    totalPages: data?.totalPages,
                    totalItems: data?.totalElements,
                    currentPage: data?.pageable?.pageNumber,
                    userSort: customSort,
                });
            }
        },
        [fetchUsers, updateUsers, userRange],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['users'](...columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
